import { DecimalPipe } from '@angular/common';
import { Component, inject, signal } from '@angular/core';
import { ActivatedRoute, RouterLink } from '@angular/router';
import { I18nService } from '../core/i18n.service';
import { SeoService } from '../core/seo.service';
import { SiteConfigService } from '../core/site-config.service';
import { MarkdownHtmlPipe } from '../shared/markdown-html.pipe';
import { FortuneService } from '../widgets/fortune/fortune.service';
import { BrandShowcaseService } from '../widgets/home/brand-showcase/brand-showcase.service';
import { ServiceMarketplaceService } from '../widgets/service-marketplace/service-marketplace.service';
import { ServiceRequestWidgetComponent } from '../widgets/service-marketplace/service-request-widget.component';

interface ServicePageItem {
  id: number;
  slug: string;
  title: string;
  description: string;
  imageUrl?: string | null;
  images?: Array<{ url?: string | null }>;
  durationMinutes?: number;
  price?: number | null;
  currency?: string;
  providerCount?: number;
  instantAvailable?: boolean;
  quoteAvailable?: boolean;
}

@Component({
  selector: 'app-service-detail-page',
  imports: [DecimalPipe, MarkdownHtmlPipe, RouterLink, ServiceRequestWidgetComponent],
  template: `
    <main class="service-detail">
      @if (detail(); as item) {
        <nav class="crumbs"><a [routerLink]="i18n.routePath('home')">{{ i18n.t('home') }}</a><span>/</span><a [routerLink]="i18n.routePath('services')">{{ site.text('nav_services','Hizmetler') }}</a><span>/</span><strong>{{ item.title }}</strong></nav>
        <section class="service-hero">
          @if (image(item)) { <div class="media"><img [src]="image(item)" [alt]="item.title"/></div> }
          @else if (site.config().businessMode !== 'serviceMarketplace') { <div class="media"><strong>{{ site.config().branding.mark }}</strong></div> }
          <article>
            <p>{{ site.config().businessMode==='serviceMarketplace' ? 'DOĞRU HİZMET VERENİ BUL' : site.text('serviceDetailEyebrow', 'Hizmet detayı') }}</p>
            <h1>{{ item.title }}</h1>
            <div class="facts">
              @if (item.providerCount) { <span><b>{{ item.providerCount }}</b> aktif hizmet veren</span> }
              @if (item.durationMinutes) { <span>{{ item.durationMinutes }} {{ site.text('minutesLabel','dakika') }} itibarıyla</span> }
              @if (item.price !== null && item.price !== undefined) { <strong>{{ item.price | number:'1.0-2' }} {{ item.currency }}'den başlayan</strong> }
              @if (item.quoteAvailable) { <span>Ücretsiz teklif</span> }
            </div>
            <div class="description" [innerHTML]="item.description | markdownHtml"></div>
            @if (site.config().businessMode !== 'serviceMarketplace') {
              <a class="appointment" [routerLink]="i18n.routePath('appointment')" [queryParams]="{serviceID: item.id}">{{ site.text('nav_appointment','Randevu al') }}</a>
            } @else {
              <a class="request-link" href="#hizmet-talebi">Hemen talep oluştur <span>↓</span></a>
            }
          </article>
        </section>
        @if (site.config().businessMode==='serviceMarketplace') {
          <div id="hizmet-talebi"><app-service-request-widget [listingID]="item.id" [title]="item.title" [instantAvailable]="!!item.instantAvailable" [quoteAvailable]="!!item.quoteAvailable" /></div>
        }
      } @else if (loading()) {
        <section class="empty">{{ site.text('serviceDetailLoading','Hizmet detayı hazırlanıyor.') }}</section>
      } @else {
        <section class="empty error"><strong>Bu hizmet şu anda yayında değil.</strong><span>{{ error() || 'Aktif hizmet veren bulunan diğer hizmetleri inceleyebilirsin.' }}</span><a [routerLink]="i18n.routePath('services')">Hizmetlere dön</a></section>
      }
    </main>
  `,
  styles: [`
    :host{display:block;min-width:0}.service-detail{display:grid;gap:28px;min-width:0}.crumbs{display:flex;flex-wrap:wrap;gap:9px;color:var(--muted);font-size:.86rem}.crumbs a{color:var(--accent)}.service-hero{display:grid;gap:clamp(28px,5vw,60px);grid-template-columns:1fr}.media{align-items:center;aspect-ratio:16/7;background:color-mix(in srgb,var(--accent) 12%,var(--surface));border-radius:42px;display:flex;justify-content:center;overflow:hidden}.media img{height:100%;object-fit:cover;width:100%}.media>strong{color:var(--accent);font:700 clamp(5rem,14vw,12rem)/1 Georgia,serif}.service-hero article{display:grid;gap:20px;max-width:920px}.service-hero article>p{color:var(--accent);font-size:.72rem;font-weight:950;letter-spacing:.16em;margin:0;text-transform:uppercase}.service-hero h1{font:700 clamp(2.8rem,7vw,7rem)/.9 Georgia,serif;letter-spacing:-.07em;margin:0}.facts{align-items:center;border-block:1px solid var(--border);display:flex;flex-wrap:wrap;gap:18px;padding:16px 0}.facts span{color:var(--muted)}.facts span b{color:var(--ink)}.facts strong{color:var(--accent);font-size:1.15rem}.description{color:var(--muted);font-size:1.08rem;line-height:1.85}.description :is(h1,h2,h3){color:var(--text);font-family:Georgia,serif;line-height:1.1;margin:1.4em 0 .55em}.description p{margin:0 0 1em}.description ul{padding-left:1.25em}.description a{color:var(--accent)}.appointment,.request-link{background:var(--accent);border-radius:999px;color:var(--accent-contrast,var(--paper));font-weight:950;justify-self:start;padding:16px 25px}.request-link span{margin-left:8px}.empty{border:1px dashed var(--border);border-radius:28px;color:var(--muted);padding:40px}.empty.error{align-items:start;display:grid;gap:10px}.empty.error strong{color:var(--ink);font-size:1.4rem}.empty.error a{color:var(--accent);font-weight:900;justify-self:start}@media(max-width:760px){.media{aspect-ratio:16/10;border-radius:28px}.service-hero h1{font-size:clamp(2.6rem,14vw,4.6rem)}}
  `]
})
export class ServiceDetailPage {
  protected readonly site = inject(SiteConfigService);
  protected readonly i18n = inject(I18nService);
  private readonly fortune = inject(FortuneService);
  private readonly showcase = inject(BrandShowcaseService);
  private readonly marketplace = inject(ServiceMarketplaceService);
  private readonly route = inject(ActivatedRoute);
  private readonly seo = inject(SeoService);
  protected readonly detail = signal<ServicePageItem | null>(null);
  protected readonly loading = signal(true);
  protected readonly error = signal('');

  constructor() { void this.load(); }

  protected image(item: ServicePageItem): string {
    return item.imageUrl || item.images?.[0]?.url || '';
  }

  private async load(): Promise<void> {
    const slug = this.route.snapshot.paramMap.get('slug') || '';
    try {
      let detail: ServicePageItem | null = null;
      if (this.site.config().businessMode === 'serviceMarketplace') {
        const item = await this.marketplace.detail(slug);
        detail = item ? {
          id: item.id,
          slug: item.slug,
          title: item.title,
          description: item.description,
          imageUrl: item.imageUrl,
          durationMinutes: item.durationMinutes,
          price: item.minPrice,
          currency: item.currency,
          providerCount: item.providerCount,
          instantAvailable: item.instantAvailable,
          quoteAvailable: item.quoteAvailable
        } : null;
      } else if (this.site.config().capabilities.commerce) {
        const [listingDetail, showcase] = await Promise.all([this.fortune.listingDetail(slug), this.showcase.data()]);
        const service = showcase.services.find((item) => item.slug === slug || String(item.id) === slug);
        detail = listingDetail ? {
          ...listingDetail,
          slug,
          imageUrl: service?.imageUrl || listingDetail.imageUrl,
          durationMinutes: service?.durationMinutes || listingDetail.durationMinutes,
          price: service?.price ?? listingDetail.price,
          currency: service?.currency || listingDetail.currency
        } : null;
      } else {
        const showcase = await this.showcase.data();
        const service = showcase.services.find((item) => item.slug === slug || String(item.id) === slug);
        detail = service ? {
          id: service.id,
          slug: service.slug || slug,
          title: service.title,
          description: service.description,
          imageUrl: service.imageUrl,
          durationMinutes: service.durationMinutes,
          price: service.price,
          currency: service.currency
        } : null;
      }
      this.detail.set(detail);
      if (detail) this.setSeo(detail, slug);
    } catch (error) {
      this.error.set(this.marketplace.errorMessage(error, 'Hizmet bilgisi alınamadı.'));
    } finally {
      this.loading.set(false);
    }
  }

  private setSeo(detail: ServicePageItem, slug: string): void {
    const config = this.site.config();
    const path = this.i18n.routePath('serviceDetail', { slug });
    const absoluteDetail = `https://${config.domain}${path}`;
    const marketplace = config.businessMode === 'serviceMarketplace';
    const actionUrl = marketplace ? `${absoluteDetail}#hizmet-talebi` : `https://${config.domain}${this.i18n.routePath('appointment')}`;
    this.seo.setPage({
      title: `${detail.title} | ${config.name}`,
      description: detail.description,
      path,
      image: this.image(detail) || undefined,
      schema: {
        '@type': 'Service',
        '@id': `${absoluteDetail}#service`,
        name: detail.title,
        description: detail.description,
        serviceType: marketplace ? 'ServiceMarketplace' : 'AppointmentService',
        provider: { '@type': marketplace ? 'Organization' : (config.brandKey === 'ceondent' ? 'Dentist' : 'LocalBusiness'), name: config.name, url: `https://${config.domain}/`, priceRange: config.seo?.priceRange || undefined, telephone: config.seo?.telephone || undefined },
        areaServed: { '@type': 'Country', name: config.country || 'TR' },
        image: this.image(detail) || undefined,
        offers: detail.price !== null && detail.price !== undefined ? { '@type': 'AggregateOffer', url: absoluteDetail, lowPrice: detail.price, priceCurrency: detail.currency || config.currency || 'TRY', offerCount: detail.providerCount || 1, availability: 'https://schema.org/InStock' } : undefined,
        potentialAction: { '@type': marketplace ? 'QuoteAction' : 'ReserveAction', target: { '@type': 'EntryPoint', urlTemplate: actionUrl } }
      }
    });
  }
}
